import type { Role } from './auth';
import { ROUTE_PERMISSIONS, DEFAULT_REDIRECT_BY_ROLE } from './auth';

export interface AppRole {
  role_key: string;
  label: string;
  default_redirect: string;
}

export interface RoutePermission {
  name?: string;
  route: string;
  label: string;
  allowed_roles: string[];
}

export interface RoleConfig {
  roles: AppRole[];
  route_permissions: RoutePermission[];
}

// Module-level store. Starts null; replaced once the API fetch completes.
// All helpers fall back to the static auth.ts values while null.
let _config: RoleConfig | null = null;

export function setRoleConfig(config: RoleConfig): void {
  _config = config;
}

export function getRoleConfig(): RoleConfig | null {
  return _config;
}

/**
 * Returns the default redirect path for a role.
 * Uses dynamic config when loaded, otherwise falls back to the static map in auth.ts.
 */
export function resolveDefaultRedirect(roleKey: string | null): string {
  if (!roleKey) return '/login';
  if (_config) {
    const found = _config.roles.find((r) => r.role_key === roleKey);
    if (found?.default_redirect) return found.default_redirect;
  }
  return DEFAULT_REDIRECT_BY_ROLE[roleKey as Role] ?? '/login';
}

/**
 * Returns true if the given role is allowed to access the given route.
 * Uses dynamic config when loaded, otherwise falls back to static ROUTE_PERMISSIONS.
 */
export function canRoleAccess(role: string | null, route: string | null): boolean {
  if (!role || !route) return false;

  if (_config) {
    const sorted = [..._config.route_permissions].sort(
      (a, b) => b.route.length - a.route.length,
    );
    for (const rp of sorted) {
      if (route === rp.route || route.startsWith(rp.route + '/')) {
        return rp.allowed_roles.includes(role);
      }
    }
    // Route not in dynamic config → no restriction defined, allow
    return true;
  }

  // Static fallback
  const sortedStatic = Object.keys(ROUTE_PERMISSIONS).sort(
    (a, b) => b.length - a.length,
  );
  for (const r of sortedStatic) {
    if (route === r || route.startsWith(r + '/')) {
      return (ROUTE_PERMISSIONS[r] as string[]).includes(role);
    }
  }
  return true;
}
